import {Banner, Button, Dialog, IconButton} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useMemo, useState} from 'react';

import {trackCrossProjectPinnedRuns} from '../../../services/analytics/crossProjectRunsEvents';
import {useViewAction} from '../../../state/views/hooks';
import * as RunSetActions from '../../../state/views/runSet/actions';
import {TOP_NAV_HEIGHT} from '../../../util/constants';
import {useMaxPinnedRuns} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {maybePluralize} from '../../../util/uihelpers';
import {
  useCurrentRunSetRef,
  usePinnedRunIdsOnly,
} from '../RunSelectorRunSetContext';
import {ProjectPicker} from './ProjectPicker';
import {RunsList} from './RunsList';
import {type ProjectOption} from './useCrossProjectOptions';

type CrossProjectRunsDrawerProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const CrossProjectRunsDrawer = ({
  isOpen,
  onOpenChange,
}: CrossProjectRunsDrawerProps) => {
  const runSetRef = useCurrentRunSetRef();
  const pinnedRunIds = usePinnedRunIdsOnly();
  const pinnedRunIdsSet = useMemo(() => new Set(pinnedRunIds), [pinnedRunIds]);
  const maxPinnedRuns = useMaxPinnedRuns();
  const isAtMaxPinsLimit = pinnedRunIds.length >= maxPinnedRuns;

  const bulkUpdatePins = useViewAction(
    runSetRef,
    RunSetActions.setBulkPinnedRuns
  );

  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(
    null
  );
  const [selectedRunIds, setSelectedRunIds] = useState(() => pinnedRunIdsSet);

  const toggleRunSelection = useCallback((runId: string) => {
    setSelectedRunIds(prev => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  }, []);

  const {runsToPin, runsToUnpin} = useMemo(() => {
    return {
      // Selected but not currently pinned -> should pin
      runsToPin: [...selectedRunIds].filter(id => !pinnedRunIdsSet.has(id)),
      // Currently pinned but not selected -> should unpin
      runsToUnpin: [...pinnedRunIdsSet].filter(id => !selectedRunIds.has(id)),
    };
  }, [pinnedRunIdsSet, selectedRunIds]);
  const hasChanges = runsToPin.length > 0 || runsToUnpin.length > 0;

  const onSubmitRuns = useCallback(() => {
    trackCrossProjectPinnedRuns({
      action: 'Cross-project runs drawer submitted',
      numRunsAdded: runsToPin.length,
      numRunsRemoved: runsToUnpin.length,
    });
    bulkUpdatePins(Array.from(selectedRunIds));
    onOpenChange(false);
  }, [bulkUpdatePins, selectedRunIds, runsToPin, runsToUnpin, onOpenChange]);

  const offsetTop =
    window.scrollY < TOP_NAV_HEIGHT ? TOP_NAV_HEIGHT - window.scrollY : 0;

  return (
    <Dialog.Drawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      offsetTop={offsetTop}>
      <TailwindContents>
        <div className="flex shrink-0 items-center justify-between gap-8 py-16 px-20 border-b border-moon-250">
          <Dialog.Heading mb={0}>Show runs from another project</Dialog.Heading>
          <IconButton
            icon="close"
            aria-label="Close drawer"
            variant="ghost"
            onPress={() => onOpenChange(false)}
          />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-20 px-20 py-16 overflow-hidden">
          <p className="shrink-0 text-moon-650">
            Select runs from another project to visualize in line plots in this
            workspace. Runs will be pinned and stay synced to source runs.
          </p>
          {isAtMaxPinsLimit ? (
            <Banner variant="warning" isDismissible={false}>
              <Banner.Body>
                <span className="font-semibold">
                  Pinned runs limit reached.
                </span>{' '}
                You can pin up to {maxPinnedRuns} runs. Unpin some runs before
                adding runs from another project.
              </Banner.Body>
            </Banner>
          ) : (
            <>
              <ProjectPicker
                selectedProject={selectedProject}
                onSelectProject={project => {
                  setSelectedProject(project);
                  setSelectedRunIds(pinnedRunIdsSet);
                }}
              />
              {selectedProject && (
                <RunsList
                  selectedProject={selectedProject}
                  selectedRunIds={selectedRunIds}
                  onToggleRun={toggleRunSelection}
                />
              )}
            </>
          )}
        </div>
        {hasChanges && (
          <div className="p-16 border-t border-moon-250 w-full">
            <Button fullWidth variant="primary" onPress={onSubmitRuns}>
              {getSubmitButtonLabel(runsToPin.length, runsToUnpin.length)}
            </Button>
          </div>
        )}
      </TailwindContents>
    </Dialog.Drawer>
  );
};

function getSubmitButtonLabel(numToPin: number, numToUnpin: number): string {
  if (numToPin > 0 && numToUnpin > 0) {
    return `Unpin ${maybePluralize(numToUnpin, 'run')} and pin ${maybePluralize(numToPin, 'new run')}`;
  }
  if (numToPin > 0) {
    return `Pin ${maybePluralize(numToPin, 'run')}`;
  }
  if (numToUnpin > 0) {
    return `Unpin ${maybePluralize(numToUnpin, 'run')}`;
  }
  return '';
}
